import { trueSkillCalculator } from './trueSkillCalculator.js';

/**
 * Calculates comprehensive player statistics from match data
 * @param {Array} players - Array of player objects
 * @param {Array} matches - Array of match objects
 * @returns {Object} Object with player IDs as keys and player stats as values
 */
export const calculatePlayerStats = (players, matches) => {
  const stats = {};

  // Initialize stats for each player with starting TrueSkill ratings
  players.forEach(p => {
    stats[p.id] = {
      ...p,
      earnedFullSlices: 0,
      earnedHalfSlices: 0,
      lifetimeFullSlices: 0,
      lifetimeHalfSlices: 0,
      paidFullSlices: p.paidFullSlices || 0,
      paidHalfSlices: p.paidHalfSlices || 0,
      history: [],
      // Start with default TrueSkill ratings for historical progression
      historicalMu: 25.0,
      historicalSigma: 8.333,
      historicalWins: 0,
      historicalLosses: 0,
    };
  });

  // Sort matches by timestamp to process in chronological order
  const sortedMatches = [...matches].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  // Process each match to calculate statistics
  sortedMatches.forEach(m => {
    if (!m || !m.score) return;

    const [s1, s2] = m.score.split('-').map(Number);
    const is10_0 = s1 === 10 && s2 === 0;
    const is10_1 = s1 === 10 && s2 === 1;

    // Award full slices for 10-0 losses (complete shutout)
    if (is10_0) {
      m.losers.forEach(id => {
        if (stats[id]) {
          stats[id].earnedFullSlices++;
          stats[id].lifetimeFullSlices++;
        }
      });
    }

    // Award half slices for 10-1 losses (close shutout)
    if (is10_1) {
      m.losers.forEach(id => {
        if (stats[id]) {
          stats[id].earnedHalfSlices++;
          stats[id].lifetimeHalfSlices++;
        }
      });
    }

    // Recalculate TrueSkill ratings chronologically for historical display
    // Get all players involved in this match
    const matchPlayerIds = [...m.winners, ...m.losers];
    const matchPlayers = matchPlayerIds
      .map(pid => ({
        id: pid,
        mu: stats[pid]?.historicalMu || 25.0,
        sigma: stats[pid]?.historicalSigma || 8.333,
        wins: stats[pid]?.historicalWins || 0,
        losses: stats[pid]?.historicalLosses || 0,
      }))
      .filter(p => stats[p.id]); // Only include players we have stats for

    if (matchPlayers.length === 4) {
      // Use actual TrueSkill calculation to get updated ratings
      const updatedPlayers = trueSkillCalculator.processMatch(m, matchPlayers);

      // Update historical progression for all players in the match
      updatedPlayers.forEach(updatedPlayer => {
        if (stats[updatedPlayer.id]) {
          // Update historical tracking
          stats[updatedPlayer.id].historicalMu = updatedPlayer.mu;
          stats[updatedPlayer.id].historicalSigma = updatedPlayer.sigma;
          stats[updatedPlayer.id].historicalWins = updatedPlayer.wins;
          stats[updatedPlayer.id].historicalLosses = updatedPlayer.losses;

          // Update current standing to reflect latest rating
          stats[updatedPlayer.id].mu = updatedPlayer.mu;
          stats[updatedPlayer.id].sigma = updatedPlayer.sigma;
          stats[updatedPlayer.id].wins = updatedPlayer.wins;
          stats[updatedPlayer.id].losses = updatedPlayer.losses;

          // Add historical data point with actual TrueSkill rating
          const isWinner = m.winners.includes(updatedPlayer.id);
          stats[updatedPlayer.id].history.push({
            date: m.date,
            timestamp: m.timestamp,
            mu: updatedPlayer.mu,
            sigma: updatedPlayer.sigma,
            result: isWinner ? 'win' : 'loss',
            score: m.score,
            matchId: m.id,
            conservativeRating: trueSkillCalculator.getConservativeRating(
              updatedPlayer.mu,
              updatedPlayer.sigma
            ),
          });
        }
      });
    }
  });

  return stats;
};
